import { Link } from "@tanstack/react-router";
import { Package } from "lucide-react";
import type { StorefrontProduct } from "@/lib/storefront";
import { formatPrice, intervalLabel } from "@/lib/storefront";

export function ProductGrid({ products, emptyMessage = "No products available yet." }: { products: StorefrontProduct[]; emptyMessage?: string }) {
  if (!products.length) {
    return (
      <div className="rounded-2xl border border-dashed border-border bg-card/50 p-10 text-center text-sm text-muted-foreground">
        {emptyMessage}
      </div>
    );
  }
  return (
    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
      {products.map((p) => (
        <ProductCard key={p.id} product={p} />
      ))}
    </div>
  );
}

export function ProductCard({ product }: { product: StorefrontProduct }) {
  const v = product.variants[0];
  return (
    <Link
      to="/p/$slug"
      params={{ slug: product.slug }}
      className="group relative flex flex-col rounded-2xl border border-border bg-card p-6 shadow-card transition hover:border-primary/40"
    >
      {product.featured && (
        <span className="absolute -top-3 left-6 rounded-full bg-gradient-brand px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white">
          Featured
        </span>
      )}
      {product.cover_url ? (
        <img src={product.cover_url} alt={product.name} className="mb-4 h-32 w-full rounded-lg object-cover" />
      ) : (
        <span className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-brand/15 text-primary ring-1 ring-border">
          <Package className="h-5 w-5" />
        </span>
      )}
      <h3 className="text-base font-semibold">{product.name}</h3>
      {product.description && (
        <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{product.description}</p>
      )}
      <div className="mt-auto pt-4">
        {v ? (
          <div className="flex items-baseline gap-1">
            <span className="text-xs text-muted-foreground">From</span>
            <span className="text-2xl font-bold">{formatPrice(v.price_cents, v.currency)}</span>
            <span className="text-xs text-muted-foreground">{intervalLabel(v.billing_interval)}</span>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">Contact for pricing</span>
        )}
      </div>
    </Link>
  );
}
